// ─── CONTACTS du calendrier (calque plein écran) ──────────────────
// Carnet partagé : liste + ajout (＋ en-tête) + swipe ✏️/🗑️ + tap = fiche/édition.
// Écoute DIRECTE de la collection `contacts` (cohérence garantie).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Alert, Linking } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran, ChoixPuces, SelecteurDate, SelecteurHeure, CalqueChoix, LigneChoix,
  lienItineraire, demanderItineraire, useUI, useT, useAuth, confirmerSuppression,
} from '@mop/core';
import {
  ecouterContacts, ajouterContact, modifierContact, supprimerContact,
  emojiRole, labelRole, nomAffiche, telPrincipal, emailPrincipal, lieuPrincipalId,
  filtrerContacts, trierContacts, TRIS_CONTACT,
} from './services/contacts';
import { ecouterLieux } from './services/lieux';
import FeuilleContact from './FeuilleContact';
import FicheContactInfo from './FicheContactInfo';

export default function EcranContacts({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [contacts, setContacts] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [feuille, setFeuille] = useState(null);   // { initial } | null (édition)
  const [fiche, setFiche] = useState(null);       // contact affiché (lecture)
  const [q, setQ] = useState('');
  const [tri, setTri] = useState('nom');
  const [dateReq, setDateReq] = useState(null);   // { valeur, onChoisir } — sélecteur de date sœur
  const [lieuReq, setLieuReq] = useState(null);   // { dispo, onChoisir } — choix d'un lieu
  const [heureReq, setHeureReq] = useState(null); // { valeur, min, onChoisir }
  const [choixReq, setChoixReq] = useState(null); // { titre, emoji, options, onChoisir }

  // Une écoute refusée renvoyait une liste vide EN SILENCE : le carnet
  // s'affichait « aucun contact » alors qu'il n'était pas lisible. On remonte
  // la cause, comme sur le Journal et la messagerie.
  const [erreurLecture, setErreurLecture] = useState('');

  const affiches = useMemo(() => trierContacts(filtrerContacts(contacts, q), tri), [contacts, q, tri]);

  useEffect(() => {
    setErreurLecture('');
    return ecouterContacts(calId, setContacts, setErreurLecture);
  }, [calId]);
  useEffect(() => ecouterLieux(calId, setLieux), [calId]);

  const enregistrer = async (c) => {
    try {
      if (feuille?.initial?.id) await modifierContact(feuille.initial.id, c);
      else await ajouterContact(calId, c, user?.uid);
      setFeuille(null);
    } catch (e) {
      Alert.alert(t('contacts.erreur'), e?.message || t('contacts.erreurEnregistrer'));
    }
  };
  const supprimer = (c) => confirmerSuppression(t('contacts.supprimerTitre'), t('contacts.supprimerConfirm', { nom: nomAffiche(c) }),
    async () => { try { await supprimerContact(c.id); } catch (_) {} });
  const ouvrir = (url) => { if (url) Linking.openURL(url).catch(() => {}); };

  // Actions rapides de la carte (seulement si la valeur principale existe).
  const actionsContact = (c) => {
    const a = [];
    const idLieu = lieuPrincipalId(c);
    const lo = idLieu ? lieux.find(l => l.id === idLieu) : null;
    // 🧭 : `onPress` et non `url` — un itineraire demande d'abord l'app.
    if (lo && lienItineraire({ lat: lo.lat, lng: lo.lng, adresse: lo.adresse || lo.nom })) {
      a.push({ ic: '🧭', onPress: () => demanderItineraire({ lat: lo.lat, lng: lo.lng, adresse: lo.adresse || lo.nom },
        { titre: t('lieux.itineraire'), annuler: t('commun.annuler') }) });
    }
    const tel = telPrincipal(c);
    if (tel) a.push({ ic: '📞', url: `tel:${String(tel).replace(/\s/g, '')}` });
    const mail = emailPrincipal(c);
    if (mail) a.push({ ic: '✉️', url: `mailto:${mail}` });
    return a.filter(x => x.url || x.onPress);
  };

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={t('contacts.titre')} iconeTitre="👥" onRetour={onFermer}
          action={{ icone: '＋', label: t('contacts.nouveau'), onPress: () => setFeuille({ initial: null }) }} />
        {contacts.length > 3 && (
          <View style={s.barre}>
            <TextInput style={s.recherche} value={q} onChangeText={setQ} autoCapitalize="none"
              placeholder={t('contacts.rechercher')} placeholderTextColor={UI.faint} />
            <ChoixPuces options={TRIS_CONTACT.map(x => ({ id: x.id, label: t(`contacts.tri_${x.id}`) }))}
              value={tri} onChange={setTri} />
          </View>
        )}
        <ScrollView contentContainerStyle={s.corps} keyboardShouldPersistTaps="handled">
          {/* La cause AVANT la liste : un carnet vide par refus de lecture
              n'est pas un carnet vide. */}
          {!!erreurLecture && (
            <View style={s.bandeau}>
              <Text style={s.bandeauTxt}>⚠️ {t('contacts.erreurLecture')}</Text>
              <Text style={s.bandeauDet}>{erreurLecture}</Text>
            </View>
          )}
          {erreurLecture ? null : contacts.length === 0 ? (
            <EmptyState icon="👥" texte={t('contacts.vide')} aide={t('contacts.aucunAide')} />
          ) : affiches.length === 0 ? (
            <EmptyState icon="🔎" texte={t('contacts.aucunResultat')} />
          ) : affiches.map(c => (
            <LigneSwipe key={c.id} espacement={8} enabled onEditer={() => setFeuille({ initial: c })} onSupprimer={() => supprimer(c)}>
              <View style={s.carte}>
                <TouchableOpacity style={s.gauche} activeOpacity={0.7} onPress={() => setFiche(c)}>
                  <Text style={{ fontSize: 20 }}>{emojiRole(c.role)}</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.nom} numberOfLines={1}>{nomAffiche(c)}</Text>
                    <Text style={s.sous} numberOfLines={1}>{labelRole(c.role)}{telPrincipal(c) ? ` · ${telPrincipal(c)}` : ''}</Text>
                  </View>
                </TouchableOpacity>
                {actionsContact(c).map((a, i) => (
                  <TouchableOpacity key={i} style={s.action} activeOpacity={0.6} onPress={() => (a.onPress ? a.onPress() : ouvrir(a.url))}
                    hitSlop={{ top: 6, bottom: 6, left: 4, right: 4 }}>
                    <Text style={{ fontSize: 18 }}>{a.ic}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </LigneSwipe>
          ))}
        </ScrollView>
        <FicheContactInfo visible={!!fiche} contact={fiche} lieux={lieux}
          onEditer={() => { setFeuille({ initial: fiche }); setFiche(null); }}
          onFermer={() => setFiche(null)} />
        <FeuilleContact visible={!!feuille} initial={feuille?.initial} lieux={lieux} onDemanderDate={setDateReq}
          onDemanderLieu={setLieuReq} onDemanderHeure={setHeureReq} onDemanderChoix={setChoixReq}
          onEnregistrer={enregistrer} onFermer={() => setFeuille(null)} />
        {/* Choix d'un lieu : calque au niveau de l'ECRAN. Rendu dans la feuille
            il resterait coince dedans, et deux modales natives imbriquees
            figent l'ecran. */}
        <CalqueChoix visible={!!lieuReq} emoji="📍" titre={t('contacts.ajouterLieu')}
          aide={lieuReq && !lieuReq.dispo?.length ? t('lieux.aucunAide') : undefined}
          onFermer={() => setLieuReq(null)}>
          {(lieuReq?.dispo || []).map(l => (
            <LigneChoix key={l.id} libelle={`📍 ${l.nom}`} sous={l.adresse}
              onPress={() => { lieuReq.onChoisir(l); setLieuReq(null); }} />
          ))}
        </CalqueChoix>
        <SelecteurDate visible={!!dateReq} valeur={dateReq?.valeur} avecEffacer
          onChange={(dk) => dateReq?.onChoisir(dk)} onFermer={() => setDateReq(null)} />
        {/* Pas d'« Aucune » : une borne d'horaire se corrige, elle ne se vide
            pas — c'est l'horaire entier qu'on retire, au swipe sur le champ. */}
        <SelecteurHeure visible={!!heureReq} valeur={heureReq?.valeur} min={heureReq?.min}
          onChange={(hhmm) => heureReq?.onChoisir(hhmm)} onFermer={() => setHeureReq(null)} />
        {/* Choix generique (jour d'un horaire…), au niveau de l'ECRAN. */}
        <CalqueChoix visible={!!choixReq} emoji={choixReq?.emoji} titre={choixReq?.titre || ''}
          onFermer={() => setChoixReq(null)}>
          {(choixReq?.options || []).map(o => (
            <LigneChoix key={o.id} libelle={o.libelle}
              onPress={() => { choixReq.onChoisir(o.id); setChoixReq(null); }} />
          ))}
        </CalqueChoix>
      </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  bandeau:    { backgroundColor: UI.dangerTint || '#fdecec', borderRadius: 12, borderWidth: 0.5, borderColor: UI.danger || '#e5484d', padding: 11, marginBottom: 10 },
  bandeauTxt: { fontSize: 13, fontWeight: '800', color: UI.danger || '#e5484d' },
  bandeauDet: { fontSize: 11.5, color: UI.muted, marginTop: 3 },
  barre: { paddingHorizontal: 12, paddingTop: 8, gap: 8 },
  recherche: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14.5, color: UI.ink },
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12 },
  gauche: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 11 },
  action: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.bg, borderWidth: 0.5, borderColor: UI.border },
  nom: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 1 },
});
